import { ArrowUpRight } from "lucide-react";
import { FadeUp, MaskedLine, Stagger, StaggerItem } from "../components/Reveal";
import { recognition, recognitionIntro } from "../data/content";

const medalStyles = {
  GOLD: "border-[#C9A227] text-[#8a6d10]",
  SILVER: "border-[#9A9A9A] text-[#6b6b6b]",
  BRONZE: "border-[#A0683F] text-[#A0683F]",
  WIN: "border-blue text-blue",
  RECORD: "border-ink text-ink",
};

const medalDot = {
  GOLD: "bg-[#C9A227]",
  SILVER: "bg-[#9A9A9A]",
  BRONZE: "bg-[#A0683F]",
  WIN: "bg-blue",
  RECORD: "bg-ink",
};

export default function Recognition() {
  return (
    <div className="pt-32 md:pt-44">
      <header className="mx-auto max-w-7xl px-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue">
          Recognition
        </p>
        <h1 className="mt-6 font-display font-extrabold leading-[0.95] tracking-[-0.03em] text-[clamp(3rem,9vw,8rem)]">
          <MaskedLine delay={0.15}>PROOF,</MaskedLine>
          <MaskedLine delay={0.28}>
            <span className="text-blue">NOT PRAISE.</span>
          </MaskedLine>
        </h1>
        <FadeUp delay={0.35}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
            {recognitionIntro}
          </p>
        </FadeUp>
      </header>

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <Stagger className="border-t border-ink" data-testid="recognition-list">
          {recognition.map((r, i) => (
            <StaggerItem key={r.campaign}>
              <div
                data-testid={`recognition-entry-${i}`}
                className="grid gap-8 border-b border-line py-10 transition-colors duration-300 hover:bg-fog/60 md:grid-cols-12 md:items-center md:py-14"
              >
                <p className="font-display text-4xl font-extrabold tracking-tight text-line md:col-span-1 md:text-5xl">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <div className="md:col-span-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                    {r.body}{r.year ? ` · ${r.year}` : ""}
                  </p>
                  <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink md:text-4xl">
                    {r.campaign}
                  </h2>
                  <p className="mt-3 max-w-lg text-sm leading-relaxed text-smoke">
                    {r.note}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {r.wins.map((w) => (
                      <span
                        key={w.category}
                        className={`inline-flex items-center gap-2 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${medalStyles[w.medal]}`}
                      >
                        <span className={`h-1.5 w-1.5 ${medalDot[w.medal]}`} aria-hidden="true" />
                        {w.medal} — {w.category}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-4">
                  <a
                    href={r.image}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`recognition-image-${i}`}
                    className="group block overflow-hidden border border-line"
                  >
                    <div className="relative aspect-[4/5] max-h-72 w-full overflow-hidden md:ml-auto md:max-w-56">
                      <img
                        src={r.image}
                        alt={`${r.campaign} — ${r.body}`}
                        loading="lazy"
                        className="h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                      />
                      <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-ink px-2 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-paper opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        View <ArrowUpRight size={10} />
                      </span>
                    </div>
                  </a>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <FadeUp delay={0.1}>
          <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
            Full certificates and coverage available on request.
          </p>
        </FadeUp>
      </section>
    </div>
  );
}
